import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES, ROLE_BADGE } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Users as UsersIcon } from "lucide-react";

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ nama: "", email: "", password: "", role: "admin_iprjt", aktif: true });
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openAdd = () => {
    setEditTarget(null);
    setForm({ nama: "", email: "", password: "", role: "admin_iprjt", aktif: true });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setForm({ nama: u.nama, email: u.email, password: "", role: u.role, aktif: u.aktif !== false });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editTarget) {
        const payload = { nama: form.nama, role: form.role, aktif: form.aktif };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editTarget.id}`, payload);
        toast.success("Pengguna berhasil diperbarui");
      } else {
        const payload = { nama: form.nama, email: form.email, password: form.password, role: form.role };
        await api.post("/users", payload);
        toast.success("Pengguna berhasil ditambahkan");
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleAktif = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { aktif: !(u.aktif !== false) });
      toast.success(`Akun ${u.nama} ${u.aktif !== false ? "dinonaktifkan" : "diaktifkan"}`);
      fetchUsers();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Pengguna</h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            <UsersIcon size={14} className="text-[#60C0D0]" /> Kelola akun & hak akses — {users.length} pengguna
          </p>
        </div>
        <Button size="sm" className="gap-1.5 bg-[#00B9AD] hover:bg-[#009E94] text-white" onClick={openAdd} data-testid="add-user-button">
          <Plus size={14} /> Tambah Pengguna
        </Button>
      </div>

      <Card className="border-slate-200/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="users-table">
            <thead className="bg-[#267B84] text-white">
              <tr>
                {["Nama", "Email", "Role", "Status", "Aksi"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-14 text-center text-slate-400">Memuat data...</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{u.nama}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${ROLE_BADGE[u.role]}`}>
                      {ROLES[u.role]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2.5 py-1 rounded-full border text-xs font-medium ${
                      u.aktif !== false
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                      {u.aktif !== false ? "Aktif" : "Nonaktif"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="h-8 gap-1 text-xs"
                        onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                        <Pencil size={13} /> Edit
                      </Button>
                      {u.id !== currentUser.id && (
                        <Button size="sm" variant="outline" className="h-8 text-xs"
                          onClick={() => toggleAktif(u)} data-testid={`toggle-user-${u.email}`}>
                          {u.aktif !== false ? "Nonaktifkan" : "Aktifkan"}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editTarget ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle>
            <DialogDescription className="text-xs">
              {editTarget ? "Kosongkan password jika tidak ingin mengubahnya." : "Bagikan email & password ini ke pengguna terkait."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-1">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Nama Lengkap <span className="text-red-500">*</span></Label>
              <Input value={form.nama} onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))} required data-testid="user-nama-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Email <span className="text-red-500">*</span></Label>
              <Input type="email" value={form.email} disabled={Boolean(editTarget)} required
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">
                Password {editTarget ? "(opsional)" : <span className="text-red-500">*</span>}
              </Label>
              <Input type="password" value={form.password} required={!editTarget} minLength={6}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="user-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Role <span className="text-red-500">*</span></Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editTarget && (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3.5 py-2.5">
                <Label className="text-sm text-slate-700">Akun Aktif</Label>
                <Switch checked={form.aktif} onCheckedChange={(v) => setForm((f) => ({ ...f, aktif: v }))} data-testid="user-aktif-switch" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="user-cancel-button">Batal</Button>
              <Button type="submit" disabled={saving} data-testid="user-submit-button"
                className="bg-[#00B9AD] hover:bg-[#009E94] text-white">
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
